import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Box, Text } from 'grommet';
import { FormNext } from 'grommet-icons';
import NoAuthLayout, {
  StyledSubmissionInput, StyledSubmissionField, StyledSubmissionAnchor,
} from '@Components/Layout/NoAuth';
import { useAppTheme } from '@Components/Context';
import { AppButton } from '@Components/Control';
import { fetchDemoLinkStatus, submitDemoInfo } from '@Actions';
import { paths } from '@Components/configs';

const YT_CHANNEL_RE = /youtube\.com\/(channel\/UC[\w-]{21,}|@[\w.-]+)\/?$/i;

function validateEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function validateYtChannel(v) {
  try {
    const url = new URL(v.trim());
    return (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com')
      && YT_CHANNEL_RE.test(url.href);
  } catch {
    return false;
  }
}

// status: null | 'loading' | 'not_found' | 'show_form' | 'submitting'
const DemoLandingPage = ({
  small, isAlreadyLoggedIn, submitInfo, checkStatus,
}) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useAppTheme();

  const [status, setStatus] = React.useState(null);
  const [ytError, setYtError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [serverError, setServerError] = React.useState('');

  React.useEffect(() => {
    if (isAlreadyLoggedIn) {
      navigate(paths.productInfringements, { replace: true });
      return;
    }
    setStatus('loading');
    checkStatus(token)
      .then(() => setStatus('show_form'))
      .catch(() => setStatus('not_found'));
  }, [token]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { fullName, email, ytChannelUrl } = e.value;

    let hasError = false;
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      hasError = true;
    } else {
      setEmailError('');
    }
    if (!validateYtChannel(ytChannelUrl)) {
      setYtError('Please enter a valid YouTube channel URL (e.g. youtube.com/@handle or youtube.com/channel/UC…).');
      hasError = true;
    } else {
      setYtError('');
    }
    if (hasError) return;

    setStatus('submitting');
    setServerError('');
    submitInfo(token, { fullName, email, ytChannelUrl })
      .then(() => navigate(paths.demoSplash, { replace: true }))
      .catch((err) => {
        setServerError(err.message || 'Something went wrong. Please try again.');
        setStatus('show_form');
      });
  };

  if (status === null || status === 'loading') {
    return (
      <NoAuthLayout seoConfig={null} title="Redflag AI Demo" small={small} customTitle>
        <Box align="center" justify="center" pad={{ vertical: '2rem' }}>
          <Text size="0.875rem" color={theme.page.secondaryText}>Checking your link…</Text>
        </Box>
      </NoAuthLayout>
    );
  }

  if (status === 'not_found') {
    return (
      <NoAuthLayout seoConfig={null} title="Redflag AI Demo" small={small}>
        <Box align="center" gap="0.75rem" pad={{ vertical: '0.5rem' }}>
          <Text
            size="0.875rem"
            color={theme.page.secondaryText}
            textAlign="center"
            style={{ lineHeight: 1.6 }}
          >
            This demo link has expired or is no longer active.
            Please reach out to your Redflag contact to request a new one.
          </Text>
          <Box direction="row" gap="0.25rem" align="center">
            <FormNext size="0.75rem" color={theme.page.altHighlight} style={{ paddingTop: '1px' }} />
            <StyledSubmissionAnchor
              label="Already have an account? Sign in"
              path={paths.login}
              color={theme.page.primaryText}
              hoverColor={theme.page.altHighlight}
            />
          </Box>
        </Box>
      </NoAuthLayout>
    );
  }

  return (
    <NoAuthLayout
      seoConfig={null}
      title="Access demo"
      subTitle="Fill in your details to explore the platform."
      small={small}
    >
      <Form onSubmit={handleSubmit}>
        <StyledSubmissionField required name="fullName" htmlFor="fullName">
          <StyledSubmissionInput
            plain
            name="fullName"
            type="text"
            placeholder="Full Name"
          />
        </StyledSubmissionField>

        <StyledSubmissionField required name="email" htmlFor="email">
          <StyledSubmissionInput
            plain
            name="email"
            type="text"
            placeholder="Email"
          />
        </StyledSubmissionField>
        {emailError && (
          <Box margin={{ top: '-0.75rem', bottom: '0.75rem' }}>
            <Text size="0.78rem" color="status-critical">{emailError}</Text>
          </Box>
        )}

        <StyledSubmissionField required name="ytChannelUrl" htmlFor="ytChannelUrl">
          <StyledSubmissionInput
            plain
            name="ytChannelUrl"
            type="text"
            placeholder="YouTube Channel URL"
          />
        </StyledSubmissionField>
        {ytError && (
          <Box margin={{ top: '-0.75rem', bottom: '0.75rem' }}>
            <Text size="0.78rem" color="status-critical">{ytError}</Text>
          </Box>
        )}

        {serverError && (
          <Box margin={{ bottom: '0.75rem' }}>
            <Text size="0.78rem" color="status-critical">{serverError}</Text>
          </Box>
        )}

        <AppButton
          overrideHover
          type="submit"
          level="authSubmit"
          bgColor={theme.button.primaryBg}
          color={theme.button.primaryText}
          label={status === 'submitting' ? 'Please wait…' : 'Continue'}
          disabled={status === 'submitting'}
          alignSelf="center"
        />
      </Form>
    </NoAuthLayout>
  );
};

DemoLandingPage.propTypes = {
  small: PropTypes.bool.isRequired,
  isAlreadyLoggedIn: PropTypes.bool,
  submitInfo: PropTypes.func.isRequired,
  checkStatus: PropTypes.func.isRequired,
};

DemoLandingPage.defaultProps = {
  isAlreadyLoggedIn: false,
};

const mapStateToProps = (state) => ({
  isAlreadyLoggedIn: state.auth?.isDemo && state.auth?.isAuthenticated,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  submitInfo: submitDemoInfo,
  checkStatus: fetchDemoLinkStatus,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DemoLandingPage);
